import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Upload, Loader2, Download } from 'lucide-react';
import { capabilityMeta, adminUploadCapability, capabilityDownloadUrl } from '../../api';
import { useToast } from '../../hooks/use-toast';

const MAX_CAP_BYTES = 15 * 1024 * 1024;

const CapabilityCard = () => {
  const { toast } = useToast();
  const [meta, setMeta] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { setMeta(await capabilityMeta()); }
    catch (err) { console.warn('Capability meta load failed:', err?.message || err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onUpload = useCallback(async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'PDF required', description: 'Only PDF files are accepted.', variant: 'destructive' });
      return;
    }
    if (f.size > MAX_CAP_BYTES) {
      toast({ title: 'File too large', description: 'Maximum 15MB.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      await adminUploadCapability(f);
      toast({ title: 'Capability statement updated', description: 'New version is live on the public site.' });
      await load();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }, [load, toast]);

  return (
    <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 p-7">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-5 h-5 text-cyan-400" />
        <h3 className="text-xl font-semibold text-white">Capability Statement</h3>
      </div>
      <p className="text-sm text-slate-400 mb-5">PDF shown on the public homepage. Uploading a new file replaces the current one.</p>
      {meta?.available ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-cyan-400/15 bg-[#080d18] px-4 py-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{meta.filename}</p>
              <p className="text-xs text-slate-500">{(meta.size / 1024 / 1024).toFixed(2)} MB &middot; Updated {new Date(meta.updated_at).toLocaleString()}</p>
            </div>
          </div>
          <a href={capabilityDownloadUrl()} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 text-xs inline-flex items-center gap-1 whitespace-nowrap"><Download className="w-3.5 h-3.5" /> View</a>
        </div>
      ) : (
        <p className="text-sm text-slate-500 mb-4">No capability statement uploaded yet.</p>
      )}
      <label className={`flex items-center gap-3 cursor-pointer rounded-md border border-dashed border-slate-700 hover:border-cyan-400 bg-[#080d18] px-4 py-3 transition ${uploading ? 'opacity-70 pointer-events-none' : ''}`}>
        {uploading ? <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" /> : <Upload className="w-4 h-4 text-cyan-400" />}
        <span className="text-sm text-slate-300 flex-1">{uploading ? 'Uploading...' : 'Click to upload new PDF (max 15MB)'}</span>
        <input type="file" accept=".pdf,application/pdf" onChange={onUpload} className="hidden" disabled={uploading} />
      </label>
    </div>
  );
};

export default CapabilityCard;
